import React, {useCallback, useEffect, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import Button from 'src/components-new/primitives/Button'
import AddIcon from '@mui/icons-material/Add'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {buildColumns, DEFAULT_COLUMNS, PatientEnrollmentRow} from './patientEnrollmentColumns'
import PatientEnrollmentDialogNew, {PatientEnrollmentFormValues} from './PatientEnrollmentDialogNew'
import {getActiveColumnLabels, PATIENT_ENROLLMENT_STATUS_OPTIONS, syncFiltersWithColumns} from './enrollmentHubOptions'

// Same value/label pairs as the legacy filter config
// (src/components/templates/EnrollmentPatientTable/EnrollmentPatientTable.tsx lines 67-84).
// Kept in sync with the PSP's columnOptions at render time (see syncFiltersWithColumns
// below) — every id here already matches its column id 1:1, so no FILTER_COLUMN_ID map
// is needed.
const FILTER_CONFIG: FilterItem[] = [
  {id: 'lastName', label: 'Cognome Paziente', type: 'string'},
  {id: 'firstName', label: 'Nome Paziente', type: 'string'},
  {id: 'status', label: 'Stato', type: 'select', options: PATIENT_ENROLLMENT_STATUS_OPTIONS},
]

/**
 * New-style equivalent of src/pages/patientEnrollment/PatientEnrollment.tsx +
 * EnrollmentPatientTable.tsx. Tab 1 ("Iscrizione nuovo paziente") of the Enrollment Hub.
 * No row click / detail action, matching the legacy behavior.
 */
export default function PatientEnrollmentTab(): JSX.Element {
  const columnOptions = useAppSelector(
    state => state.pspConfigReducer.data.enrollmentHubConfigurations?.patientEnrollmentConfigurations?.columnOptions
  )
  const toast = useToast()

  const [rows, setRows] = useState<PatientEnrollmentRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-createdAt'})
  const [createOpen, setCreateOpen] = useState(false)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getPatientEnrollmentRequest(activeFilters, setStatus, pagination),
      apiClient.getPatientEnrollmentRequestCount(activeFilters, setStatusCount),
    ])
      .then(([data, countResult]) => {
        if (Array.isArray(data)) setRows(data)
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleSaveRequest = async ({firstName, lastName, birthDate, files}: PatientEnrollmentFormValues) => {
    const formData = new FormData()
    formData.append('firstName', firstName)
    formData.append('lastName', lastName)
    formData.append('birthDate', birthDate)
    files.forEach((file) => formData.append('file', file, file.name))

    let finalStatus: string | undefined
    await apiClient.postPatientEnrollment(formData, (s: string) => { finalStatus = s })
    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Richiesta inviata con successo.')
      setCreateOpen(false)
      fetchData()
    } else {
      toast.error("Errore nell'invio della richiesta.")
    }
  }

  const columns: ColumnDef<PatientEnrollmentRow>[] = buildColumns(columnOptions)

  // Filters are kept in sync with the PSP's columnOptions: a filter only shows up if its
  // column is active, and it adopts that column's label.
  const columnLabels = getActiveColumnLabels(columnOptions, DEFAULT_COLUMNS)
  const filterConfig = syncFiltersWithColumns(FILTER_CONFIG, columnLabels)

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        <Button variant="primary" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          {'Nuova'}
        </Button>
        {filterConfig.length > 0 && <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />}
      </Box>

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare le richieste di iscrizione.'}
        </InlineMessage>
      )}

      <DataTable<PatientEnrollmentRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessuna richiesta trovata"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortBy={pagination.order}
        onSortChange={(order) => setPagination(prev => ({...prev, order, page: 0}))}
      />

      {createOpen && (
        <PatientEnrollmentDialogNew
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSave={handleSaveRequest}
        />
      )}
    </Box>
  )
}
